/**
 * authInterceptor.js
 *
 * Axios 401 interceptor — auto token refresh.
 *
 * When any API call returns 401 the interceptor:
 *  1. Refreshes the guest/platform token via /auth/tokenguest
 *  2. If a user is logged in, refreshes the bookie token via /auth/bookietoken
 *  3. Retries the failed request with the correct token (guest or bookie)
 *  4. Queues any concurrent 401s so only one refresh is ever in flight
 *
 * Dependencies are injected explicitly to keep this module testable and decoupled
 * from main.js bootstrap concerns (Dependency Inversion Principle).
 */

let _isRefreshing = false;
let _pendingQueue = [];

function _resolveQueue(guestToken, bookieToken) {
    _pendingQueue.forEach(({ originalToken, oldGuestToken, resolve }) => {
        const token = originalToken === oldGuestToken
            ? guestToken
            : (bookieToken || guestToken);
        resolve(token);
    });
    _pendingQueue = [];
}

function _rejectQueue(err) {
    _pendingQueue.forEach(({ reject }) => reject(err));
    _pendingQueue = [];
}

// Estrazione della logica (Single Responsibility Principle)
function isTargetApi(config, targetBaseUrls) {
    if (!config || !config.url) return false;

    // Axios potrebbe usare config.baseURL, quindi costruiamo l'URL completo per il check
    const fullUrl = config.baseURL && !config.url.startsWith('http')
        ? `${config.baseURL}${config.url}`
        : config.url;

    // Controlla se l'URL della chiamata inizia con uno dei nostri target URL validi
    return targetBaseUrls.some(baseUrl => baseUrl && fullUrl.startsWith(baseUrl));
}

async function _refreshTokens({ axios, store, refreshUrl, platformId, userId, VUE_APP_URL_API }) {
    try {
        // 1. Refresh guest/platform token
        const { data } = await axios.post(refreshUrl, { token: localStorage.getItem('refresh') });
        localStorage.setItem('token',   data.accessToken);
        localStorage.setItem('expired', data.expired);
        store.dispatch('settings/setToken', data.accessToken);

        let bookieToken = null;

        // 2. Refresh bookie token se c'è un utente
        if (userId && +userId !== 0) {
            try {
                const { data: bk } = await axios.post(
                    `${VUE_APP_URL_API}/auth/bookietoken`,
                    { platform: platformId, userid: +userId },
                    { headers: { Authorization: `Bearer ${data.accessToken}` } }
                );
                localStorage.setItem('logged_token',   bk.accessToken);
                localStorage.setItem('logged_expired', bk.expired);
                store.commit('user/SET_USER_TOKEN', bk.accessToken);
                bookieToken = bk.accessToken;
            } catch (e) {
                // PRIMA ERA UN WARN SILENTE. Ora loggiamo come errore.
                // Se fallisce il refresh del bookie, l'utente dovrebbe essere sloggato.
                console.error('[auth] ERRORE CRITICO: Bookie token refresh fallito.', e.response?.data || e.message);
                throw e; // Rilanciamo l'errore per far fallire correttamente la coda
            }
        }

        return { guestToken: data.accessToken, bookieToken };
    } catch (error) {
        console.error('[auth] ERRORE: Refresh token base fallito.', error.response?.data || error.message);
        throw error;
    }
}

export function installAuthInterceptor({ axios, store, router, refreshUrl, VUE_APP_URL_API, VUE_APP_URL_RULES, platformId, userId }) {
    // Raccogliamo solo gli URL validi, escludendo eventuali undefined
    const targetUrls = [VUE_APP_URL_API, VUE_APP_URL_RULES].filter(Boolean);

    axios.interceptors.response.use(
        response => response, // Zero impatto sulle chiamate di successo
        async error => {
            const orig = error.config;

            // 1. Non è un 401 oppure è già stato ritentato? Ignora e propaga l'errore
            if (error.response?.status !== 401 || orig._retry) {
                return Promise.reject(error);
            }

            // 2. È una chiamata verso i domini che vogliamo gestire?
            if (!isTargetApi(orig, targetUrls)) {
                return Promise.reject(error); // Se è un altro dominio, non facciamo nulla
            }

            // 3. È una chiamata di auth? Evitiamo loop infiniti
            if (orig.url?.includes('/auth/')) {
                console.error(`[auth] 401 su endpoint di auth (${orig.url}). Interrompo il retry per evitare loop.`);
                return Promise.reject(error);
            }

            orig._retry = true;
            const oldGuestToken = localStorage.getItem('token');
            const originalToken = (orig.headers['Authorization'] || '').replace('Bearer ', '');

            if (_isRefreshing) {
                return new Promise((resolve, reject) => {
                    _pendingQueue.push({ originalToken, oldGuestToken, resolve, reject });
                }).then(retryToken => {
                    orig.headers['Authorization'] = `Bearer ${retryToken}`;
                    return axios(orig);
                });
            }

            _isRefreshing = true;

            try {
                const { guestToken, bookieToken } = await _refreshTokens({
                    axios, store, refreshUrl, platformId, userId, VUE_APP_URL_API
                });

                _resolveQueue(guestToken, bookieToken);

                const retryToken = originalToken === oldGuestToken
                    ? guestToken
                    : (bookieToken || guestToken);

                orig.headers['Authorization'] = `Bearer ${retryToken}`;
                return axios(orig);
            } catch (refreshError) {
                console.error('[auth] Processo di Token Refresh fallito completamente.', refreshError);
                _rejectQueue(refreshError);
                // Pulizia localStorage qui potrebbe essere utile prima di redirezionare
                router.push('/e/100');
                return Promise.reject(refreshError);
            } finally {
                _isRefreshing = false;
            }
        }
    );
}